import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Users, GamepadIcon, Calendar, TrendingUp, TrendingDown, MoreVertical, Edit, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { EditGroupDialog } from "./EditGroupDialog";
import { DeleteGroupDialog } from "./DeleteGroupDialog";

interface Group {
  id: string;
  name: string;
  created_at: string;
  playerCount?: number;
  activeGamesCount?: number;
}

interface GroupStats {
  totalGames: number;
  totalProfit: number;
  playerCount: number;
  activeGames: number;
}

interface User {
  id: string;
  email?: string;
}

interface GroupCardProps {
  group: Group;
  user: User;
  onUpdate: () => void;
}

export const GroupCard = ({ group, user, onUpdate }: GroupCardProps) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<GroupStats>({
    totalGames: 0,
    totalProfit: 0,
    playerCount: 0,
    activeGames: 0
  });
  const [loading, setLoading] = useState(true);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  // Track if any dialog is open to prevent card navigation
  const isAnyDialogOpen = showEditDialog || showDeleteDialog;

  useEffect(() => {
    console.log('🚀 GroupCard useEffect triggered for group:', group.id, group.name);
    fetchGroupStats();
  }, [group.id]);

  const fetchGroupStats = async () => {
    console.log('🔍 Starting fetchGroupStats for group:', group.id, group.name);
    console.log('👤 User prop passed to GroupCard:', user?.id, user?.email);
    
    try {
      setLoading(true);
      
      if (!user) {
        console.log('❌ No user prop available');
        setLoading(false);
        return;
      }

      // Get player count
      const { count: playerCount, error: playerCountError } = await supabase
        .from('players')
        .select('*', { count: 'exact', head: true })
        .eq('group_id', group.id);
      
      console.log('👥 Player count:', playerCount, playerCountError ? `Error: ${playerCountError.message}` : '');

      // Get active games count
      const { count: activeGames, error: activeGamesError } = await supabase
        .from('games')
        .select('*', { count: 'exact', head: true })
        .eq('group_id', group.id)
        .eq('status', 'in_progress');
      
      console.log('🎮 Active games:', activeGames, activeGamesError ? `Error: ${activeGamesError.message}` : '');

      // Get total games count
      const { count: totalGames, error: totalGamesError } = await supabase
        .from('games')
        .select('*', { count: 'exact', head: true })
        .eq('group_id', group.id);
      
      console.log('🎯 Total games:', totalGames, totalGamesError ? `Error: ${totalGamesError.message}` : '');

      // Get user's profit from settled games in this group
      const { data: settledGames, error: settledGamesError } = await supabase
        .from('games')
        .select('id')
        .eq('group_id', group.id)
        .eq('status', 'settled');
      
      console.log('✅ Settled games:', settledGames?.length, settledGames, settledGamesError ? `Error: ${settledGamesError.message}` : '');

      let totalProfit = 0;
      if (settledGames && settledGames.length > 0) {
        const gameIds = settledGames.map(g => g.id);
        console.log('🎲 Game IDs for profit calculation:', gameIds);
        
        // Find current user as a player in this group
        const { data: userPlayer, error: userPlayerError } = await supabase
          .from('players')
          .select('id')
          .eq('group_id', group.id)
          .eq('auth_user_id', user.id)
          .maybeSingle();
        
        console.log('🧑‍🤝‍🧑 User player record:', userPlayer, userPlayerError ? `Error: ${userPlayerError.message}` : '');

        if (userPlayer) {
          const { data: payouts, error: payoutsError } = await supabase
            .from('payouts')
            .select('net_amount')
            .eq('player_id', userPlayer.id)
            .in('game_id', gameIds);
          
          console.log('💰 Payouts for user:', payouts, payoutsError ? `Error: ${payoutsError.message}` : '');

          totalProfit = payouts?.reduce((sum, p) => {
            console.log('Adding payout:', p.net_amount, 'Running sum:', sum);
            return sum + (p.net_amount || 0);
          }, 0) || 0;
          
          console.log('💵 Final total profit:', totalProfit);
        } else {
          console.log('⚠️ User is not a player in this group');
        }
      } else {
        console.log('ℹ️ No settled games found');
      }

      const finalStats = {
        totalGames: totalGames || 0,
        totalProfit,
        playerCount: playerCount || 0,
        activeGames: activeGames || 0
      };
      
      console.log('📊 Final stats to set:', finalStats);
      
      setStats(finalStats);
    } catch (error) {
      console.error('❌ Error fetching group stats:', error);
      // Set default stats to prevent blank UI
      setStats({
        totalGames: 0,
        totalProfit: 0,
        playerCount: 0,
        activeGames: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Card className="hover:shadow-md transition-all duration-200 cursor-pointer" 
          onClick={() => {
            if (!isAnyDialogOpen) {
              navigate(`/group/${group.id}`);
            }
          }}>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="truncate">{group.name}</span>
          <div className="flex items-center space-x-2">
            <div className="flex items-center text-sm text-muted-foreground">
              <Calendar className="h-4 w-4 mr-1" />
              {formatDate(group.created_at)}
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-8 w-8 p-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                <DropdownMenuItem onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setShowEditDialog(true);
                }}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Group
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setShowDeleteDialog(true);
                  }}
                  className="text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Group
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center text-sm text-muted-foreground">
              <Users className="h-4 w-4 mr-1" />
              {stats.playerCount} players
            </div>
            <div className="flex items-center text-sm text-muted-foreground">
              <GamepadIcon className="h-4 w-4 mr-1" />
              {stats.activeGames} active
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Total Games:</span>
            <span className="font-medium">{stats.totalGames}</span>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Your P&L:</span>
            {loading ? (
              <div className="h-5 w-16 bg-muted animate-pulse rounded" />
            ) : (
              <div className="flex items-center space-x-1">
                {stats.totalProfit > 0 ? (
                  <TrendingUp className="h-4 w-4 text-green-500" />
                ) : stats.totalProfit < 0 ? (
                  <TrendingDown className="h-4 w-4 text-red-500" />
                ) : null}
                <span className={`font-medium ${
                  stats.totalProfit > 0 ? 'text-green-600' : 
                  stats.totalProfit < 0 ? 'text-red-600' : 
                  'text-muted-foreground'
                }`}>
                  {stats.totalProfit !== 0 && (stats.totalProfit > 0 ? '+' : '')}₹{Math.abs(stats.totalProfit).toFixed(2)}
                </span>
              </div>
            )}
          </div>
        </div>
        <Button 
          className="w-full" 
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/group/${group.id}`);
          }}
        >
          View Group
        </Button>
      </CardContent>

      <EditGroupDialog
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        group={group}
        onGroupUpdated={onUpdate}
      />

      <DeleteGroupDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        group={group}
        onGroupDeleted={onUpdate}
      />
    </Card>
  );
};